import {
  ProFormSelect,
  ProFormDatePicker,
  ProFormDateTimeRangePicker,
  ProFormRadio,
  ProFormText,
  ProFormItem,
  ProFormGroup,
} from "@ant-design/pro-components";
import { Input } from "antd";
import { SearchOutlined, ArrowRightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import type { ProFormSelectProps } from "@ant-design/pro-components";

export interface OptionType {
  label: string;
  value: string | number;
}

const SELECT_BOX_PLACEHOLDER = "Select...";

// ---------- COMMON ----------
interface CommonFilterProps {
  label: string;
  fieldName: string;
  filterValueToGqlMapper?: (value: any) => {};
  defaultValue?: any;
  isArrayRequired?: boolean;
}

// ---------- SELECT ----------
export interface SelectFilterProps extends CommonFilterProps {
  filterType: "SELECT";
  options: OptionType[];
  filterProps?: ProFormSelectProps["fieldProps"];
}

export const getSelectFilter = (filter: SelectFilterProps) => {
  const { label, fieldName, options, filterProps } = filter;

  return (
    <ProFormSelect
      key={fieldName}
      name={fieldName}
      label={label}
      placeholder={SELECT_BOX_PLACEHOLDER}
      fieldProps={{
        showSearch: true,
        allowClear: true,
        options,
        optionFilterProp: "label",
        ...filterProps,
      }}
    />
  );
};

// ---------- RADIO ----------
export interface RadioFilterProps extends CommonFilterProps {
  filterType: "RADIO";
  options: OptionType[];
}

export const getRadioFilter = (filter: RadioFilterProps) => {
  const { label, fieldName, options } = filter;

  return <ProFormRadio.Group key={fieldName} name={fieldName} label={label} options={options} />;
};

// ---------- DATE ----------
export interface DatePickerFilterProps extends CommonFilterProps {
  filterType: "DATE";
}

export const getDatePickerFilter = (filter: DatePickerFilterProps) => {
  const { label, fieldName } = filter;

  return <ProFormDatePicker key={fieldName} name={fieldName} label={label} />;
};

// ---------- DATE_RANGE ----------
export interface DateRangePickerFilterProps extends CommonFilterProps {
  filterType: "DATE_RANGE";
}

export const getDateRangePickerFilter = (filter: DateRangePickerFilterProps) => {
  const { label, fieldName } = filter;

  return (
    <ProFormDateTimeRangePicker
      key={fieldName}
      name={fieldName}
      label={label}
      transform={(value, namePath, allValues) => {
        if (Array.isArray(value) && typeof value[0] === "string" && typeof value[1] === "string") {
          return {
            [namePath]: [dayjs(value[0]), dayjs(value[1])],
          };
        }
        return allValues;
      }}
      fieldProps={{
        placeholder: ["Start date", "End date"],
        separator: <ArrowRightOutlined />,
      }}
    />
  );
};

// ---------- SEARCH ----------
export interface SearchConfigProps {
  searchType?: "single" | "options";
  searchField?: string;
  searchOptions?: OptionType[];
  inputPlaceholder?: string;
}

export const getSearchAreaFilter = (
  config: SearchConfigProps,
  onSearch?: (searchValue: string) => void,
) => {
  const { searchType = "options", searchField = "searchValue" } = config;

  if (searchType === "single" && onSearch) {
    return (
      <ProFormItem name={searchField}>
        <Input.Search />
      </ProFormItem>
    );
  }

  if (searchType === "options") {
    return (
      <ProFormGroup>
        <ProFormSelect
          name="searchField"
          placeholder={SELECT_BOX_PLACEHOLDER}
          fieldProps={{
            showSearch: true,
            allowClear: true,
            options: config.searchOptions,
          }}
        />
        <ProFormText
          name="searchValue"
          placeholder="Enter here"
          fieldProps={{
            allowClear: true,
            suffix: <SearchOutlined />,
          }}
        />
      </ProFormGroup>
    );
  }

  return (
    <ProFormText
      name={searchField}
      placeholder={config.inputPlaceholder}
      fieldProps={{
        allowClear: true,
        suffix: <SearchOutlined />,
      }}
    />
  );
};

// ---------- Union & Exports ----------
export type FilterComponentProps =
  | SelectFilterProps
  | RadioFilterProps
  | DatePickerFilterProps
  | DateRangePickerFilterProps;

export type FilterData = FilterComponentProps;

export type FilterConfig = {
  filters?: FilterData[];
  submitRenderer?: SearchConfigProps;
};
